import { Calendar, Phone, MapPin, Video, Check, Trash2, X, DoorOpen, Sparkles, Eye, EyeOff } from "lucide-react";

var TYPE_ICONS = { call: Phone, presencial: MapPin, video: Video };
var TYPE_LABELS = { call: "Ligacao", presencial: "Presencial", video: "Video-chamada" };
var TYPE_COLORS = { call: "bg-blue-100 text-blue-700", presencial: "bg-emerald-100 text-emerald-700", video: "bg-purple-100 text-purple-700" };
var STATUS_COLORS = { agendado: "bg-blue-100 text-blue-700", realizado: "bg-emerald-100 text-emerald-700", cancelado: "bg-gray-100 text-gray-500", no_show: "bg-red-100 text-red-700" };
var STATUS_LABELS = { agendado: "Agendado", realizado: "Realizado", cancelado: "Cancelado", no_show: "No-show" };
var EVENT_TYPE_LABELS = { feira: "Feira", jantar: "Jantar", palestra: "Palestra", treinamento: "Treinamento", outro: "Evento" };

function formatTime(d) {
  if (!d) return "";
  try { return new Date(d).toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" }); } catch (e) { return ""; }
}

export function ScheduleCard({ item, isAdmin, onStatusChange, onDelete }) {
  var Icon = TYPE_ICONS[item.schedule_type] || Calendar;
  var typeCls = TYPE_COLORS[item.schedule_type] || "bg-gray-100 text-gray-700";
  var typeLabel = TYPE_LABELS[item.schedule_type] || item.schedule_type;
  var statusCls = STATUS_COLORS[item.status] || "bg-gray-100 text-gray-700";
  var statusLabel = STATUS_LABELS[item.status] || item.status;
  var time = formatTime(item.date);
  var isUp = item.status === "agendado";

  return (
    <div className={"bg-white rounded-xl border p-4 transition-all " + (isUp ? "border-[#3B82F6]/20 shadow-sm" : "border-gray-100")} data-testid={"schedule-card-" + item.id}>
      <div className="flex items-start justify-between mb-3">
        <div className="flex items-center gap-3">
          <div className={"p-2 rounded-lg " + typeCls}><Icon className="w-4 h-4" /></div>
          <div>
            <p className="text-sm font-semibold text-[#0A2F3D]">{item.client_name}</p>
            <p className="text-xs text-gray-400">{typeLabel} - {time}</p>
          </div>
        </div>
        <span className={"px-2 py-1 text-xs font-medium rounded-full " + statusCls}>{statusLabel}</span>
      </div>
      {item.notes && <p className="text-xs text-gray-500 mb-3 bg-gray-50 rounded-lg px-3 py-2">{item.notes}</p>}
      {isAdmin && item.broker_name && <p className="text-xs text-gray-400 mb-2">Corretor: {item.broker_name}</p>}
      {isUp && (
        <div className="flex gap-2 pt-2 border-t border-gray-100">
          <button onClick={function () { onStatusChange(item.id, "realizado"); }} className="flex items-center gap-1 px-3 py-1.5 bg-emerald-50 text-emerald-700 rounded-lg text-xs font-medium hover:bg-emerald-100" data-testid={"schedule-done-" + item.id}><Check className="w-3 h-3" /> Realizado</button>
          <button onClick={function () { onStatusChange(item.id, "no_show"); }} className="flex items-center gap-1 px-3 py-1.5 bg-red-50 text-red-600 rounded-lg text-xs font-medium hover:bg-red-100" data-testid={"schedule-noshow-" + item.id}>No-show</button>
          <button onClick={function () { onStatusChange(item.id, "cancelado"); }} className="flex items-center gap-1 px-3 py-1.5 bg-gray-50 text-gray-500 rounded-lg text-xs font-medium hover:bg-gray-100" data-testid={"schedule-cancel-" + item.id}>Cancelar</button>
          <button onClick={function () { onDelete(item.id); }} className="ml-auto p-1.5 text-gray-300 hover:text-red-500 hover:bg-red-50 rounded-lg" data-testid={"schedule-delete-" + item.id}><Trash2 className="w-3.5 h-3.5" /></button>
        </div>
      )}
    </div>
  );
}

export function RoomCard({ item, canCancel, onCancel }) {
  var endH = item.start_hour + (item.duration_hours || 1);
  return (
    <div className="bg-amber-50 rounded-xl border border-amber-200 p-4" data-testid={"room-card-" + item.id}>
      <div className="flex items-start justify-between mb-2">
        <div className="flex items-center gap-3">
          <div className="p-2 rounded-lg bg-amber-100"><DoorOpen className="w-4 h-4 text-amber-700" /></div>
          <div>
            <p className="text-sm font-semibold text-[#0A2F3D]">{item.client_name}</p>
            <p className="text-xs text-amber-600 font-medium">Sala - {String(item.start_hour).padStart(2,"0")}:00 - {String(endH).padStart(2,"0")}:00</p>
          </div>
        </div>
        <span className="px-2 py-1 text-xs font-medium rounded-full bg-amber-100 text-amber-700">Sala</span>
      </div>
      <p className="text-xs text-amber-600 mb-1">Corretor: {item.broker_name}</p>
      {item.notes && <p className="text-xs text-gray-500 bg-white/50 rounded-lg px-3 py-2 mb-2">{item.notes}</p>}
      {canCancel && item.status === "reservado" && (
        <div className="flex pt-2 border-t border-amber-200">
          <button onClick={function () { onCancel(item.id); }} className="flex items-center gap-1 px-3 py-1.5 bg-red-50 text-red-600 rounded-lg text-xs font-medium hover:bg-red-100" data-testid={"room-cancel-" + item.id}><X className="w-3 h-3" /> Cancelar Reserva</button>
        </div>
      )}
    </div>
  );
}

export function EventCard({ item, isAdmin, onDelete }) {
  var color = item.color || "#6B7280";
  var typeLabel = EVENT_TYPE_LABELS[item.event_type] || item.event_type;
  return (
    <div className="rounded-xl border p-4" style={{ borderColor: color + "40", backgroundColor: color + "10" }} data-testid={"event-card-" + item.id}>
      <div className="flex items-start justify-between mb-2">
        <div className="flex items-center gap-3">
          <div className="p-2 rounded-lg" style={{ backgroundColor: color + "20" }}><Sparkles className="w-4 h-4" style={{ color: color }} /></div>
          <div>
            <p className="text-sm font-semibold text-[#0A2F3D]">{item.title}</p>
            <p className="text-xs" style={{ color: color }}>{typeLabel}</p>
          </div>
        </div>
        <div className="flex items-center gap-1">
          {item.visibility === "pessoal" ? <EyeOff className="w-3.5 h-3.5 text-gray-400" /> : <Eye className="w-3.5 h-3.5 text-gray-400" />}
          <span className="px-2 py-1 text-xs font-medium rounded-full" style={{ backgroundColor: color + "20", color: color }}>{item.visibility === "publico" ? "Publico" : "Pessoal"}</span>
        </div>
      </div>
      {item.description && <p className="text-xs text-gray-500 mb-2">{item.description}</p>}
      {isAdmin && (
        <div className="flex pt-2 border-t" style={{ borderColor: color + "30" }}>
          <button onClick={function () { onDelete(item.id); }} className="flex items-center gap-1 px-3 py-1.5 bg-red-50 text-red-600 rounded-lg text-xs font-medium hover:bg-red-100" data-testid={"event-delete-" + item.id}><Trash2 className="w-3 h-3" /> Excluir</button>
        </div>
      )}
    </div>
  );
}
